/**
 * WaiverScrollAccept - Component that requires scrolling to the bottom of waiver text before acceptance
 * 
 * This "annoying" pattern ensures users must view the entire waiver content before they can accept it.
 * The waivers are displayed directly in the form in a scrollable box.
 * 
 * @example
 * ```tsx
 * <WaiverScrollAccept
 *   isChecked={watch("terms")}
 *   onCheckedChange={(checked) => setValue("terms", checked)}
 *   error={errors.terms?.message}
 * />
 * ```
 */

"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, Flex, FormControl, FormLabel, Text } from "@chakra-ui/react";
import { Checkbox } from "@/components";
import { useThemeTokens } from "@/lib/theme/tokens";
import { useWaiversGetPublic, useTenantData } from "@/hooks";
import { getTenantDisplayName } from "@/lib/utils/tenant";
import {
  getPlaceholderWaiverDisplayText,
  PLACEHOLDER_WAIVER_NAME,
  PLACEHOLDER_WAIVER_POLICY_ID,
} from "@/lib/constants/placeholder-waiver";
import { Heading, TouchAwareTooltip } from "@/components/ui";
import { LoadingState } from "@/components/feedback/LoadingState";

interface WaiverScrollAcceptProps {
  /**
   * Whether the checkbox is checked
   */
  isChecked: boolean;
  /**
   * Callback when checkbox state changes
   */
  onCheckedChange: (checked: boolean) => void;
  /**
   * Error message to display
   */
  error?: string;
}

export const WaiverScrollAccept = ({
  isChecked,
  onCheckedChange,
  error,
}: WaiverScrollAcceptProps) => {
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const theme = useThemeTokens();

  const { tenantData } = useTenantData();
  const tenantDisplayName = getTenantDisplayName(tenantData) || "This studio";

  const { items: waivers, isLoading } = useWaiversGetPublic();
  const hasWaivers = Boolean(waivers?.length);

  const displayWaivers = useMemo(() => {
    if (hasWaivers && waivers?.length) return waivers;
    return [
      {
        policyId: PLACEHOLDER_WAIVER_POLICY_ID,
        name: PLACEHOLDER_WAIVER_NAME,
        displayText: getPlaceholderWaiverDisplayText(tenantDisplayName),
      },
    ];
  }, [hasWaivers, waivers, tenantDisplayName]);

  // Attach scroll listener and check initial position
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container || displayWaivers.length === 0) {
      setHasScrolledToBottom(false);
      return;
    }

    const checkScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      // Allow small tolerance (10px) for rounding differences
      const isAtBottom = scrollHeight - scrollTop - clientHeight <= 10;
      
      // Once user scrolls to bottom, keep checkbox enabled (one-way flag)
      if (isAtBottom) {
        setHasScrolledToBottom(true);
      }
    };

    // Initial check after a small delay to ensure DOM is ready
    const initialTimer = setTimeout(() => {
      checkScroll();
    }, 100);

    // Attach scroll listener
    container.addEventListener("scroll", checkScroll);

    return () => {
      clearTimeout(initialTimer);
      container.removeEventListener("scroll", checkScroll);
    };
  }, [displayWaivers]);

  const handleCheckboxChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (hasScrolledToBottom) {
        onCheckedChange(event.target.checked);
      }
    },
    [hasScrolledToBottom, onCheckedChange],
  );

  if (isLoading) {
    return (
      <Box>
        <LoadingState message="Loading waivers..." minH="200px" />
      </Box>
    );
  }

  return (
    <Box>
      <Text fontSize="sm" color={theme.text.primary} mb={4} fontWeight="500">
        Please read the following waivers carefully. You must scroll to the bottom of all waivers before you can accept them.
      </Text>

      <Box
        ref={scrollContainerRef}
        maxH="400px"
        overflowY="auto"
        p={{ base: 4, md: 6 }}
        borderRadius="8px"
        borderWidth="1px"
        borderColor={theme.borderGlass.default}
        boxShadow={theme.shadow.card}
        mb={4}
        css={{
          "&::-webkit-scrollbar": {
            width: "8px",
          },
          "&::-webkit-scrollbar-track": {
            background: "transparent",
          },
          "&::-webkit-scrollbar-thumb": {
            background: theme.borderGlass.default,
            borderRadius: "4px",
            "&:hover": {
              background: theme.text.tertiary,
            },
          },
        }}
      >
        <Flex direction="column" gap={6}>
          {displayWaivers.map((waiver) => (
            <Box key={String(waiver.policyId)}>
              <Heading size={{ base: "md", md: "lg" }} mb={4} color={theme.text.primary}>
                {waiver.name}
              </Heading>
              <Box
                height="1px"
                bg={theme.borderGlass.default}
                mb={4}
              />
              <Text
                fontSize={{ base: "sm", md: "md" }}
                whiteSpace="pre-line"
                lineHeight="1.7"
                color={theme.text.secondary}
              >
                {waiver.displayText}
              </Text>
            </Box>
          ))}
        </Flex>
      </Box>

      <FormControl isInvalid={!!error}>
        <TouchAwareTooltip
          label="Scroll to the bottom of the waivers before you can check this."
          isDisabled={hasScrolledToBottom}
          placement="top"
          openDelay={200}
          hasArrow
        >
          <Flex
            columnGap="5px"
            align="flex-start"
            w="fit-content"
            cursor={hasScrolledToBottom ? "pointer" : "not-allowed"}
          >
            <Checkbox
              mt="1px"
              id="terms"
              isChecked={isChecked}
              onChange={handleCheckboxChange}
              isDisabled={!hasScrolledToBottom}
              pointerEvents={hasScrolledToBottom ? undefined : "none"}
            />
            <FormLabel htmlFor="terms" mb="0" cursor="inherit" fontSize="sm">
              <Text variant="primary" fontSize="sm" color={theme.text.primary}>
                I have read and agree to the Waivers
              </Text>
            </FormLabel>
          </Flex>
        </TouchAwareTooltip>
        {error && (
          <Text color={theme.accent.primary} fontSize="sm" mt="6px">
            {error}
          </Text>
        )}
      </FormControl>
    </Box>
  );
};

